import React, { Suspense, lazy, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { useAuthStore } from './store/authStore';
import { hasPermission } from './lib/permissions';
import { AppInitializer } from './components/AppInitializer';
import { PortalLanguageGate } from './customer-portal/PortalLanguageGate';
import { PortalLocaleProvider } from './customer-portal/i18n/PortalLocaleProvider';
import { isPmToolHost, isPublicSiteHost, redirectStaffToPmTool } from './lib/appHost';

// Staff shell - lazy for the mirror-image reason the portal is: customers never execute
// any of this, and eagerly importing it put the whole admin chrome in front of the
// portal's first paint.
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const Layout = lazy(() => import('./components/Layout').then((m) => ({ default: m.Layout })));

// Customer portal - lazy-loaded like every staff page below, so staff users (who never execute
// any of this) don't pay for ~9,000+ lines of portal code in the eager entry bundle.
const CustomerPortalLayout = lazy(() =>
  import('./customer-portal/CustomerPortalLayout').then((m) => ({ default: m.CustomerPortalLayout })),
);
const PortalAuthPage = lazy(() =>
  import('./customer-portal/PortalAuthPage').then((m) => ({ default: m.PortalAuthPage })),
);
const PortalAuthGuard = lazy(() =>
  import('./customer-portal/PortalAuthGuard').then((m) => ({ default: m.PortalAuthGuard })),
);
const CustomerProjectsPage = lazy(() =>
  import('./customer-portal/CustomerProjectsPage').then((m) => ({ default: m.CustomerProjectsPage })),
);
const CustomerAllProjectsPage = lazy(() =>
  import('./customer-portal/CustomerAllProjectsPage').then((m) => ({ default: m.CustomerAllProjectsPage })),
);
const CustomerProjectDocumentsPage = lazy(() =>
  import('./customer-portal/CustomerProjectDocumentsPage').then((m) => ({ default: m.CustomerProjectDocumentsPage })),
);
const CustomerProjectDetailPage = lazy(() =>
  import('./customer-portal/CustomerProjectDetailPage').then((m) => ({ default: m.CustomerProjectDetailPage })),
);
const CustomerProfilePage = lazy(() =>
  import('./customer-portal/CustomerProfilePage').then((m) => ({ default: m.CustomerProfilePage })),
);
const CustomerDriveApplyPage = lazy(() =>
  import('./customer-portal/CustomerDriveApplyPage').then((m) => ({ default: m.CustomerDriveApplyPage })),
);
const CustomerQuotationListPage = lazy(() =>
  import('./customer-portal/CustomerQuotationListPage').then((m) => ({ default: m.CustomerQuotationListPage })),
);
const CustomerQuotationRequestPage = lazy(() =>
  import('./customer-portal/CustomerQuotationRequestPage').then((m) => ({ default: m.CustomerQuotationRequestPage })),
);
const CustomerQuotationDetailPage = lazy(() =>
  import('./customer-portal/CustomerQuotationDetailPage').then((m) => ({ default: m.CustomerQuotationDetailPage })),
);
const CustomerSiteClearanceRequestPage = lazy(() =>
  import('./customer-portal/CustomerSiteClearanceRequestPage').then((m) => ({
    default: m.CustomerSiteClearanceRequestPage,
  })),
);
const CustomerToolsPage = lazy(() =>
  import('./customer-portal/CustomerToolsPage').then((m) => ({ default: m.CustomerToolsPage })),
);
const CustomerRewardsPage = lazy(() =>
  import('./customer-portal/CustomerRewardsPage').then((m) => ({ default: m.CustomerRewardsPage })),
);
const CustomerMessagesPage = lazy(() =>
  import('./customer-portal/CustomerMessagesPage').then((m) => ({ default: m.CustomerMessagesPage })),
);

// Lazy load pages for code splitting
// Note: Pages use named exports, so we need to map them to default exports for lazy()
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Projects = lazy(() => import('./pages/Projects').then(module => ({ default: module.Projects })));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails').then(module => ({ default: module.ProjectDetails })));
const ProjectDocumentsPage = lazy(() =>
  import('./pages/ProjectDocumentsPage').then((m) => ({ default: m.ProjectDocumentsPage })),
);
const Users = lazy(() => import('./pages/Users').then(module => ({ default: module.Users })));
const Customers = lazy(() => import('./pages/Customers').then(module => ({ default: module.Customers })));
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const PlutoLayoutPage = lazy(() => import('./pluto/pages/PlutoLayoutPage').then(module => ({ default: module.PlutoLayoutPage })));
const AttendancePage = lazy(() => import('./pluto/pages/AttendancePage').then(module => ({ default: module.AttendancePage })));
const LeavePage = lazy(() => import('./pluto/pages/LeavePage').then(module => ({ default: module.LeavePage })));
const AdminPage = lazy(() => import('./pluto/pages/AdminPage').then(module => ({ default: module.AdminPage })));
const EmployeePage = lazy(() => import('./pluto/pages/EmployeePage').then(module => ({ default: module.EmployeePage })));
const SiteAttendancePage = lazy(() => import('./pluto/pages/SiteAttendancePage').then(module => ({ default: module.SiteAttendancePage })));
const LeadsListPage = lazy(() => import('./crm/pages/LeadsListPage').then(module => ({ default: module.LeadsListPage })));
const PipelineTasksPage = lazy(() =>
  import('./crm/pages/PipelineTasksPage').then((module) => ({ default: module.PipelineTasksPage })),
);
const LeadDetailPage = lazy(() => import('./crm/pages/LeadDetailPage').then(module => ({ default: module.LeadDetailPage })));
const CrmSettingsPage = lazy(() => import('./crm/pages/CrmSettingsPage').then(module => ({ default: module.CrmSettingsPage })));
const CrmAnalyticsPage = lazy(() =>
  import('./crm/pages/CrmAnalyticsPage').then(module => ({ default: module.CrmAnalyticsPage })),
);
const BooksAnalyticsPage = lazy(() =>
  import('./crm/pages/BooksAnalyticsPage.tsx').then((m) => ({ default: m.BooksAnalyticsPage })),
);
const ProjectsAnalyticsPage = lazy(() =>
  import('./crm/pages/ProjectsAnalyticsPage').then((m) => ({ default: m.ProjectsAnalyticsPage })),
);
const SiteMapPage = lazy(() =>
  import('./crm/pages/SiteMapPage').then((m) => ({ default: m.SiteMapPage })),
);
const MorningDashboardPage = lazy(() =>
  import('./crm/pages/MorningDashboardPage').then((m) => ({ default: m.MorningDashboardPage })),
);
const CrmCompaniesPage = lazy(() =>
  import('./crm/pages/CrmCompaniesPage').then((m) => ({ default: m.CrmCompaniesPage }))
);
const CrmBillingPage = lazy(() =>
  import('./crm/pages/CrmBillingPage').then((m) => ({ default: m.CrmBillingPage }))
);
const CompanyDetailPage = lazy(() =>
  import('./crm/pages/CompanyDetailPage').then((m) => ({ default: m.CompanyDetailPage }))
);
const ContactDetailPage = lazy(() =>
  import('./pages/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage }))
);
const VendorsPage = lazy(() =>
  import('./pages/inventory/VendorsPage').then((m) => ({ default: m.VendorsPage }))
);
const WarehousePage = lazy(() =>
  import('./pages/inventory/WarehousePage').then((m) => ({ default: m.WarehousePage }))
);
const LaboursPage = lazy(() =>
  import('./pages/inventory/LaboursPage').then((m) => ({ default: m.LaboursPage }))
);
const LabourProfilePage = lazy(() =>
  import('./pages/inventory/LabourProfilePage').then((m) => ({ default: m.LabourProfilePage }))
);
const OrganizationAboutPage = lazy(() =>
  import('./pages/Placeholders').then((module) => ({ default: module.OrganizationAboutPage }))
);
const OrganizationBuildTemplatesPage = lazy(() =>
  import('./pages/OrganizationBuildTemplatesPage').then((m) => ({ default: m.OrganizationBuildTemplatesPage }))
);
const OrganizationCategoryLibraryPage = lazy(() =>
  import('./pages/OrganizationCategoryLibraryPage').then((m) => ({ default: m.OrganizationCategoryLibraryPage }))
);
const MarketingLandingPage = lazy(() =>
  import('./pages/MarketingLandingPage').then((m) => ({ default: m.MarketingLandingPage }))
);

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#09175d]"></div>
  </div>
);

/** Cross-domain redirect for staff who land on the public marketing site. */
function StaffPmToolRedirect({ to = '/dashboard' }: { to?: string }) {
  useEffect(() => {
    redirectStaffToPmTool(to);
  }, [to]);
  return <PageLoader />;
}

/** Staff PM tool routes - only on pm-tool.manedesign.in (or local dev). */
function StaffHostGate({ children }: { children: React.ReactNode }) {
  if (isPmToolHost()) return <>{children}</>;
  return <Navigate to="/" replace />;
}

/** Staff login page - only on the PM tool host; public site sends users to portal auth. */
function LoginRoute() {
  if (!isPmToolHost()) {
    return <Navigate to="/portal/auth" replace />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Login />
    </Suspense>
  );
}

/**
 * PrivateRoute Component
 * Protects routes from unauthorized access.
 */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const sessionPending = useAuthStore((state) => state.sessionPending);

  if (sessionPending && !user) {
    return <PageLoader />;
  }

  if (user) return <>{children}</>;
  if (!isPmToolHost()) {
    return <Navigate to="/portal/auth" replace />;
  }
  return <Navigate to="/login" />;
}

/** Staff and internal roles only - customers use /portal */
function StaffOnlyRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'customer') return <Navigate to="/portal" replace />;
  return <>{children}</>;
}

function PortalPrivateRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const sessionPending = useAuthStore((state) => state.sessionPending);

  if (sessionPending && !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#09175d]" />
      </div>
    );
  }

  if (!user) return null;

  if (user.role !== 'customer') {
    if (isPmToolHost()) return <Navigate to="/dashboard" replace />;
    return <StaffPmToolRedirect to="/dashboard" />;
  }

  return <>{children}</>;
}

/** Customer-only portal routes */
function CustomerPortalRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);

  if (!user) return null;

  if (user.role !== 'customer') {
    if (isPmToolHost()) return <Navigate to="/dashboard" replace />;
    return <StaffPmToolRedirect to="/dashboard" />;
  }

  return <>{children}</>;
}

/**
 * PlutoOnlyRoute
 * Restricts site_engineer users to Pluto routes only.
 * Use SiteEngineerAllowedRoute for routes that site engineers should also access.
 */
function PlutoOnlyRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'customer') return <Navigate to="/portal" replace />;
  return user.role === 'site_engineer' ? <Navigate to="/pluto/attendance" replace /> : <>{children}</>;
}

/**
 * SiteEngineerAllowedRoute
 * Like PlutoOnlyRoute but also permits site_engineer users.
 * Used for /projects and /projects/:id so site engineers can view their assigned projects.
 */
function SiteEngineerAllowedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  return <>{children}</>;
}

/** Generic permission-gated route - used where different instances of the same kind of route need different permissions (e.g. Pluto Employees vs. Build Templates vs. Pluto Admin Panel). */
function PermissionRoute({
  requiredPermission,
  children,
}: {
  requiredPermission: string;
  children: React.ReactNode;
}) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  return hasPermission(user, requiredPermission) ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

/**
 * SiteAttendanceRoute
 * Accessible by admin and site_engineer roles.
 */
function SiteAttendanceRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin' || user.role === 'site_engineer') return <>{children}</>;
  return <Navigate to="/pluto/attendance" replace />;
}

/**
 * CrmAccessRoute
 * Accessible by admin, manager, sales.
 */
function CrmAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  if (hasPermission(user, 'sales.access')) return <>{children}</>;
  return <Navigate to="/dashboard" replace />;
}

/** Design projects list - site_manager and site_engineer excluded; freelancers included (scoped in UI). */
function DesignAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (hasPermission(user, 'projects.design')) {
    return <>{children}</>;
  }
  return <Navigate to="/projects/build" replace />;
}

/** Routes freelancers must not access (build, CRM, Pluto, inventory, etc.). */
function NonFreelancerRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  return <>{children}</>;
}

/**
 * BooksAnalyticsAccessRoute - financial analytics (collections, aged receivables).
 * project_manager excluded; they access project metrics via the Dashboard panel.
 */
function BooksAnalyticsAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  if (hasPermission(user, 'analytics.books')) return <>{children}</>;
  return <Navigate to="/dashboard" replace />;
}

/**
 * SalesAnalyticsAccessRoute - CRM/sales funnel analytics.
 * project_manager excluded so the entire Analytics section is inaccessible to that role.
 */
function SalesAnalyticsAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  if (hasPermission(user, 'analytics.sales')) return <>{children}</>;
  return <Navigate to="/dashboard" replace />;
}

/** Design-projects portfolio analytics. project_manager excluded - they use the Dashboard panel instead. */
function ProjectsAnalyticsAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  if (hasPermission(user, 'analytics.projects')) {
    return <>{children}</>;
  }
  return <Navigate to="/dashboard" replace />;
}

/** Morning dashboard - design-only daily standup. Same roles as Projects analytics. */
function MorningDashboardAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  if (hasPermission(user, 'analytics.overview')) {
    return <>{children}</>;
  }
  return <Navigate to="/dashboard" replace />;
}

/**
 * InventoryAccessRoute - material catalog, warehouse, vendors.
 * Excludes site_engineer (they use the project Materials tab).
 */
function InventoryAccessRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'freelancer') return <Navigate to="/projects/design" replace />;
  if (hasPermission(user, 'inventory.access')) {
    return <>{children}</>;
  }
  return <Navigate to="/dashboard" replace />;
}

/** Legacy pay page → project detail (payment is inline on detail) */
function PortalSubmissionPayRedirect() {
  const { leadId } = useParams<{ leadId: string }>();
  if (!leadId) return <Navigate to="/portal/submissions" replace />;
  return <Navigate to={`/portal/submissions/${leadId}`} replace />;
}

/** Legacy `/portal/applications/*` → `/portal/submissions/*` */
function LegacyApplicationsRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/portal\/applications\/?/, '');
  const target = rest ? `/portal/submissions/${rest}` : '/portal/submissions';
  return <Navigate to={`${target}${location.search}`} replace />;
}

/** Legacy vendor portal paths → customer portal */
function LegacyVendorRedirect() {
  return <Navigate to="/portal/auth" replace />;
}

/** Legacy `/documents/:projectId` → project documents (same access as /projects) */
function LegacyProjectDocumentsRedirect() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return <Navigate to="/projects" replace />;
  return <Navigate to={`/projects/${projectId}/documents`} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * App Component
 * Handles routing for the entire application.
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <AppInitializer>
      <BrowserRouter basename="/">
        <AnalyticsTracker />
        <PortalLocaleProvider>
        <Routes>
          <Route
            path="/"
            element={<RootRoute />}
          />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/customer-login" element={<Navigate to="/portal/auth" replace />} />
          <Route path="/customer-signup" element={<Navigate to="/portal/auth" replace />} />

          <Route
            path="/portal/auth"
            element={
              <PortalLanguageGate>
                <Suspense fallback={<PageLoader />}>
                  <PortalAuthPage />
                </Suspense>
              </PortalLanguageGate>
            }
          />

          <Route
            path="/portal"
            element={
              <PortalLanguageGate>
                <Suspense fallback={<PageLoader />}>
                  <PortalAuthGuard>
                    <CustomerPortalLayout />
                  </PortalAuthGuard>
                </Suspense>
              </PortalLanguageGate>
            }
          >
            <Route index element={<PortalPrivateRoute><Suspense fallback={<PageLoader />}><CustomerProjectsPage /></Suspense></PortalPrivateRoute>} />
            <Route path="onboarding" element={<Navigate to="/portal" replace />} />
            <Route path="set-password" element={<Navigate to="/portal" replace />} />
            <Route path="photos" element={<Navigate to="/portal" replace />} />
            <Route path="tools" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerToolsPage /></Suspense></CustomerPortalRoute>} />
            {/* Legacy tool stubs → Tools cooking page (no 404s, no fake catalog) */}
            <Route path="tools/apply-ec" element={<Navigate to="/portal/tools" replace />} />
            <Route path="tools/apply-sanction-plan" element={<Navigate to="/portal/tools" replace />} />
            <Route path="tools/apply-bescom-meter" element={<Navigate to="/portal/tools" replace />} />
            <Route path="tools/apply-bwssb-meter" element={<Navigate to="/portal/tools" replace />} />
            <Route path="tools/khata-calculator" element={<Navigate to="/portal/tools" replace />} />
            <Route path="drive/gba" element={<Navigate to="/portal/drive/gba/apply" replace />} />
            <Route path="submissions" element={<PortalPrivateRoute><Suspense fallback={<PageLoader />}><CustomerQuotationListPage /></Suspense></PortalPrivateRoute>} />
            <Route path="submissions/new" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerQuotationRequestPage /></Suspense></CustomerPortalRoute>} />
            <Route path="submissions/site-clearance" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerSiteClearanceRequestPage /></Suspense></CustomerPortalRoute>} />
            <Route path="submissions/outputs" element={<Navigate to="/portal/submissions" replace />} />
            <Route path="submissions/:leadId/pay" element={<CustomerPortalRoute><PortalSubmissionPayRedirect /></CustomerPortalRoute>} />
            <Route path="submissions/:leadId" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerQuotationDetailPage /></Suspense></CustomerPortalRoute>} />
            <Route path="applications/*" element={<LegacyApplicationsRedirect />} />
            <Route path="drive/gba/apply" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerDriveApplyPage /></Suspense></CustomerPortalRoute>} />
            <Route path="projects" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerAllProjectsPage /></Suspense></CustomerPortalRoute>} />
            <Route path="projects/:id/documents" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerProjectDocumentsPage /></Suspense></CustomerPortalRoute>} />
            <Route path="projects/:id" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerProjectDetailPage /></Suspense></CustomerPortalRoute>} />
            <Route path="profile" element={<PortalPrivateRoute><Suspense fallback={<PageLoader />}><CustomerProfilePage /></Suspense></PortalPrivateRoute>} />
            <Route path="rewards" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerRewardsPage /></Suspense></CustomerPortalRoute>} />
            <Route path="messages" element={<CustomerPortalRoute><Suspense fallback={<PageLoader />}><CustomerMessagesPage /></Suspense></CustomerPortalRoute>} />
            <Route path="settings" element={<Navigate to="/portal/profile" replace />} />
          </Route>

          <Route path="/portal/partner" element={<Navigate to="/portal/auth" replace />} />
          <Route path="/portal/dashboard/*" element={<LegacyVendorRedirect />} />

          {/* Protected staff routes - PM tool host only */}
          <Route
            element={
              <StaffHostGate>
                <PrivateRoute>
                  <StaffOnlyRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Layout />
                    </Suspense>
                  </StaffOnlyRoute>
                </PrivateRoute>
              </StaffHostGate>
            }
          >
            {/* Main application routes */}
            <Route 
              path="dashboard"
              element={
                <SiteEngineerAllowedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <Dashboard />
                  </Suspense>
                </SiteEngineerAllowedRoute>
              } 
            />
            <Route
              path="projects/design"
              element={
                <DesignAccessRoute>
                  <SiteEngineerAllowedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Projects />
                    </Suspense>
                  </SiteEngineerAllowedRoute>
                </DesignAccessRoute>
              }
            />
            <Route
              path="projects/build"
              element={
                <NonFreelancerRoute>
                  <SiteEngineerAllowedRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Projects />
                    </Suspense>
                  </SiteEngineerAllowedRoute>
                </NonFreelancerRoute>
              }
            />
            <Route
              path="projects"
              element={
                <SiteEngineerAllowedRoute>
                  <ProjectsIndexRedirect />
                </SiteEngineerAllowedRoute>
              }
            />
            <Route
              path="projects/:id/documents"
              element={
                <SiteEngineerAllowedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <ProjectDocumentsPage />
                  </Suspense>
                </SiteEngineerAllowedRoute>
              }
            />
            <Route 
              path="projects/:id" 
              element={
                <SiteEngineerAllowedRoute>
                  <Suspense fallback={<PageLoader />}>
                    <ProjectDetails />
                  </Suspense>
                </SiteEngineerAllowedRoute>
              } 
            />
            <Route 
              path="users" 
              element={
                <NonFreelancerRoute>
                  <PlutoOnlyRoute>
                    <Suspense fallback={<PageLoader />}>
                      <Users />
                    </Suspense>
                  </PlutoOnlyRoute>
                </NonFreelancerRoute>
              } 
            />
            <Route path="documents" element={<Navigate to="/projects" replace />} />
            <Route
              path="documents/:projectId"
              element={
                <SiteEngineerAllowedRoute>
                  <LegacyProjectDocumentsRedirect />
                </SiteEngineerAllowedRoute>
              }
            />
            <Route 
              path="customers" 
              element={
                <NonFreelancerRoute>
                  <CrmAccessRoute>
                    <PlutoOnlyRoute>
                      <Suspense fallback={<PageLoader />}>
                        <Customers />
                      </Suspense>
                    </PlutoOnlyRoute>
                  </CrmAccessRoute>
                </NonFreelancerRoute>
              } 
            />
            <Route
              path="customers/:id"
              element={
                <NonFreelancerRoute>
                  <CrmAccessRoute>
                    <PlutoOnlyRoute>
                      <Suspense fallback={<PageLoader />}>
                        <ContactDetailPage />
                      </Suspense>
                    </PlutoOnlyRoute>
                  </CrmAccessRoute>
                </NonFreelancerRoute>
              }
            />
            <Route 
              path="settings" 
              element={
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              } 
            />
            <Route
              path="pluto"
              element={
                <NonFreelancerRoute>
                  <Suspense fallback={<PageLoader />}>
                    <PlutoLayoutPage />
                  </Suspense>
                </NonFreelancerRoute>
              }
            >
              <Route index element={<Navigate to="/pluto/attendance" replace />} />
              <Route
                path="attendance"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <AttendancePage />
                  </Suspense>
                }
              />
              <Route
                path="leave"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <LeavePage />
                  </Suspense>
                }
              />
              <Route
                path="admin"
                element={
                  <PermissionRoute requiredPermission="pluto.admin">
                    <Suspense fallback={<PageLoader />}>
                      <AdminPage />
                    </Suspense>
                  </PermissionRoute>
                }
              />
              <Route
                path="employees"
                element={
                  <PermissionRoute requiredPermission="pluto.employees">
                    <Suspense fallback={<PageLoader />}>
                      <EmployeePage />
                    </Suspense>
                  </PermissionRoute>
                }
              />
              <Route
                path="site-attendance"
                element={
                  <SiteAttendanceRoute>
                    <Suspense fallback={<PageLoader />}>
                      <SiteAttendancePage />
                    </Suspense>
                  </SiteAttendanceRoute>
                }
              />
            </Route>
            
            {/* CRM routes */}
            <Route
              path="crm"
              element={
                <CrmAccessRoute>
                  <Navigate to="/crm/leads" replace />
                </CrmAccessRoute>
              }
            />
            <Route
              path="crm/leads"
              element={
                <CrmAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <LeadsListPage />
                  </Suspense>
                </CrmAccessRoute>
              }
            />
            <Route
              path="crm/tasks"
              element={
                <CrmAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <PipelineTasksPage />
                  </Suspense>
                </CrmAccessRoute>
              }
            />
            <Route
              path="crm/leads/:id"
              element={
                <CrmAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <LeadDetailPage />
                  </Suspense>
                </CrmAccessRoute>
              }
            />
            <Route
              path="crm/analytics"
              element={<Navigate to="/analytics/morning" replace />}
            />
            <Route
              path="analytics"
              element={<Navigate to="/analytics/morning" replace />}
            />
            <Route
              path="analytics/morning"
              element={
                <MorningDashboardAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <MorningDashboardPage />
                  </Suspense>
                </MorningDashboardAccessRoute>
              }
            />
            <Route
              path="analytics/sales"
              element={
                <SalesAnalyticsAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <CrmAnalyticsPage />
                  </Suspense>
                </SalesAnalyticsAccessRoute>
              }
            />
            <Route
              path="analytics/books"
              element={
                <BooksAnalyticsAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <BooksAnalyticsPage />
                  </Suspense>
                </BooksAnalyticsAccessRoute>
              }
            />
            <Route
              path="analytics/projects"
              element={
                <ProjectsAnalyticsAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <ProjectsAnalyticsPage />
                  </Suspense>
                </ProjectsAnalyticsAccessRoute>
              }
            />
            <Route
              path="analytics/site-map"
              element={
                <ProjectsAnalyticsAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <SiteMapPage />
                  </Suspense>
                </ProjectsAnalyticsAccessRoute>
              }
            />
            <Route
              path="crm/settings"
              element={
                <PermissionRoute requiredPermission="crm.settings">
                  <Suspense fallback={<PageLoader />}>
                    <CrmSettingsPage />
                  </Suspense>
                </PermissionRoute>
              }
            />
            <Route
              path="crm/companies"
              element={
                <CrmAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <CrmCompaniesPage />
                  </Suspense>
                </CrmAccessRoute>
              }
            />
            <Route
              path="crm/companies/:id"
              element={
                <CrmAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <CompanyDetailPage />
                  </Suspense>
                </CrmAccessRoute>
              }
            />
            <Route
              path="crm/billing"
              element={
                <CrmAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <CrmBillingPage />
                  </Suspense>
                </CrmAccessRoute>
              }
            />

            <Route
              path="inventory/vendors"
              element={
                <InventoryAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <VendorsPage />
                  </Suspense>
                </InventoryAccessRoute>
              }
            />
            <Route
              path="inventory/warehouse"
              element={
                <InventoryAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <WarehousePage />
                  </Suspense>
                </InventoryAccessRoute>
              }
            />
            <Route
              path="inventory/labours"
              element={
                <InventoryAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <LaboursPage />
                  </Suspense>
                </InventoryAccessRoute>
              }
            />
            <Route
              path="inventory/labours/:workerId"
              element={
                <InventoryAccessRoute>
                  <Suspense fallback={<PageLoader />}>
                    <LabourProfilePage />
                  </Suspense>
                </InventoryAccessRoute>
              }
            />

            <Route
              path="organization/build-templates"
              element={
                <PermissionRoute requiredPermission="organization.build_templates">
                  <Suspense fallback={<PageLoader />}>
                    <OrganizationBuildTemplatesPage />
                  </Suspense>
                </PermissionRoute>
              }
            />
            <Route
              path="organization/category-library"
              element={
                <PermissionRoute requiredPermission="organization.category_library">
                  <Suspense fallback={<PageLoader />}>
                    <OrganizationCategoryLibraryPage />
                  </Suspense>
                </PermissionRoute>
              }
            />
            <Route
              path="organization/about"
              element={
                <Suspense fallback={<PageLoader />}>
                  <OrganizationAboutPage />
                </Suspense>
              }
            />

          </Route>
        </Routes>
        </PortalLocaleProvider>
      </BrowserRouter>
    </AppInitializer>
    </QueryClientProvider>
  );
}

export default App;

/** Public homepage - marketing for guests; signed-in users go to their portal. */
function RootRoute() {
  const user = useAuthStore((state) => state.user);

  if (user?.role === 'customer') {
    return <Navigate to="/portal" replace />;
  }

  if (user) {
    if (isPmToolHost()) return <Navigate to="/dashboard" replace />;
    return <StaffPmToolRedirect to="/dashboard" />;
  }

  if (isPublicSiteHost()) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MarketingLandingPage />
      </Suspense>
    );
  }

  if (isPmToolHost()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <MarketingLandingPage />
    </Suspense>
  );
}

function ProjectsIndexRedirect() {
  const user = useAuthStore((state) => state.user);
  if (user?.role === 'site_engineer' || user?.role === 'site_manager') {
    return <Navigate to="/projects/build" replace />;
  }
  if (user?.role === 'freelancer') {
    return <Navigate to="/projects/design" replace />;
  }
  return <Navigate to="/projects/design" replace />;
}